import { daysAgo, ymd } from '@/lib/format';
import { cx, input } from '@/ui';

const PRESETS = [
  { key: '30d', label: '30D', days: 30 },
  { key: '60d', label: '60D', days: 60 },
  { key: '90d', label: '90D', days: 90 },
  { key: '1y', label: '1Y', days: 365 },
] as const;

/**
 * Date range picker in the Cleaning and Ops style: two compact date inputs plus
 * preset pills, with the active preset highlighted.
 */
export function DateRangeControl({
  start,
  end,
  onChange,
  loading,
}: {
  start: string;
  end: string;
  onChange: (start: string, end: string) => void;
  loading?: boolean;
}) {
  const today = ymd(new Date());
  const activePreset = PRESETS.find((p) => end === today && start === ymd(daysAgo(p.days - 1)))?.key ?? null;

  return (
    <div className="flex flex-wrap items-center gap-2">
      {loading && <span className="h-3 w-3 animate-spin rounded-full border border-neutral-200 border-t-neutral-900" />}
      <div className="flex items-center gap-1.5">
        <input
          type="date"
          className={input}
          value={start}
          max={end}
          disabled={loading}
          onChange={(e) => onChange(e.target.value, end)}
          aria-label="Start date"
        />
        <span className="text-xs text-neutral-400">to</span>
        <input
          type="date"
          className={input}
          value={end}
          min={start}
          max={today}
          disabled={loading}
          onChange={(e) => onChange(start, e.target.value)}
          aria-label="End date"
        />
      </div>
      <div className="flex items-center gap-0.5 rounded-xl border border-neutral-200 bg-neutral-100/70 p-0.5">
        {PRESETS.map((p) => (
          <button
            key={p.key}
            disabled={loading}
            onClick={() => onChange(ymd(daysAgo(p.days - 1)), today)}
            className={cx(
              'h-7 px-2.5 text-[12px] font-medium rounded-lg border transition-colors disabled:opacity-40',
              activePreset === p.key
                ? 'border-neutral-200 bg-white text-neutral-950 shadow-[0_1px_2px_rgb(24_35_47/0.08)]'
                : 'border-transparent text-neutral-500 hover:text-neutral-900'
            )}
          >
            {p.label}
          </button>
        ))}
      </div>
    </div>
  );
}
